import {
  Banner,
  Card,
  AlertIcon,
  Flex,
  Link,
  Skeleton,
  Typography,
} from '@iheartjane/reefer';
import { Form, useFormContext } from '@iheartjane/reefer-hook-form';
import debounce from 'lodash-es/debounce';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';

import {
  useValidateGlobalPromoCode,
  useValidatePromoCode,
} from '@jane/business-admin/data-access';
import { useGlobalSpecialsModal } from '@jane/business-admin/providers/GlobalSpecialsModalProvider';
import { useSpecialsModal } from '@jane/business-admin/providers/SpecialsModalProvider';
import { useStoreDetails } from '@jane/business-admin/providers/StoreDetailsProvider';
import {
  BODY_LINE_HEIGHT,
  MODAL_CARD_WIDTH,
  businessPaths,
} from '@jane/business-admin/util';

import {
  FORM_FIELD_DEFAULT_MARGIN,
  SHOW_PROMO_CODE_SPECIAL_TYPES,
} from './form';

const PROMO_CODE_MAX_LENGTH = 40;

interface Props {
  isCreateMode: boolean;
  isDuplicateMode: boolean;
  isGlobalSpecial?: boolean;
  isLoading: boolean;
  specialPromoCode?: string | null;
}
export const PromoCodeCard = ({
  isCreateMode,
  isDuplicateMode,
  isGlobalSpecial = false,
  isLoading,
  specialPromoCode,
}: Props) => {
  const { storeId } = useStoreDetails();
  const {
    posSyncMap: { isJanePosSynced },
    isReadOnly,
  } = useSpecialsModal();
  const { isDisabled: globalSpecialDisabled } = useGlobalSpecialsModal();
  const { special_id: specialId = '' } = useParams<'special_id'>();

  const { watch, setError, clearErrors, getValues } = useFormContext();
  const switchEnabled = watch('display.promo_code_enabled');
  const specialTypeWatch = watch('special_type');

  const { mutateAsync: validatePromoCode } = useValidatePromoCode(storeId);
  const { mutateAsync: validateGlobalSpecialPromoCode } =
    useValidateGlobalPromoCode();
  const [validationError, setValidationError] = useState<string>();
  const [existingSpecial, setExistingSpecial] = useState<{
    id: number;
    title: string;
  }>();

  const resetErrorState = () => {
    clearErrors('promo_code');
    setValidationError(undefined);
    setExistingSpecial(undefined);
  };

  const validate = useMemo(
    () =>
      debounce(
        async (promoCode) => {
          resetErrorState();

          try {
            if (isGlobalSpecial) {
              await validateGlobalSpecialPromoCode({
                promoCode,
              });
            } else {
              await validatePromoCode({
                promoCode,
                specialId:
                  isDuplicateMode || isCreateMode ? undefined : specialId,
              });
            }
          } catch (error: any) {
            if (error?.response?.status === 409) {
              const body = await error?.response.json();
              setExistingSpecial(body.existing_special);

              setValidationError(
                'This promo code is already in use. Remove this code or assign a new code to continue.'
              );
              setError('promo_code', {
                message:
                  'This promo code is already in use. Remove this code or assign a new code to continue.',
              });
            }
          }
        },
        500,
        {
          // On initial load, we want to check validation right away
          // After that, we want to check validation when user is done typing
          leading: isLoading,
          trailing: !isLoading,
        }
      ),
    [isLoading, isDuplicateMode]
  );

  useEffect(() => {
    // Reset validations if promo code is disabled
    if (!switchEnabled) {
      resetErrorState();
    }
  }, [switchEnabled]);

  const validateUniquePromoCode = (promo_code: string) => {
    if (promo_code === specialPromoCode && !isDuplicateMode) {
      return;
    }
    if (promo_code && !isJanePosSynced) {
      validate(promo_code);
    } else {
      resetErrorState();
    }
  };

  useEffect(() => {
    if (isDuplicateMode) {
      const promoCode = getValues('promo_code');
      validateUniquePromoCode(promoCode);
    }
  }, [isDuplicateMode]);

  // when creating: only show once special_type has been selected, otherwise it will show when nothing is selected
  // when editing: only hide once special_type has been set, otherwise it will lose existing special promo_code
  if (
    (isCreateMode && !specialTypeWatch) ||
    (specialTypeWatch &&
      !SHOW_PROMO_CODE_SPECIAL_TYPES.includes(specialTypeWatch))
  ) {
    return null;
  }

  const existingSpecialLink = isGlobalSpecial
    ? businessPaths.globalSpecialDetail(existingSpecial?.id?.toString() || '')
    : businessPaths.storeSpecialDetail(
        storeId,
        existingSpecial?.id?.toString() || ''
      );

  const isDisabled = isJanePosSynced || isReadOnly || globalSpecialDisabled;

  return (
    <Card border="grays-light" width={MODAL_CARD_WIDTH} mb={32}>
      <Card.Content>
        <Flex p={24} flexDirection="column">
          <>
            <Flex mb={12}>
              <Typography variant="h2-semibold">Promo Code</Typography>
              <Typography ml={4} variant="h2-regular" color="grays-mid">
                (Optional)
              </Typography>
            </Flex>
            {switchEnabled && existingSpecial && (
              <Banner
                mb={20}
                variant="error"
                label={
                  <Flex>
                    <Typography variant="body-semibold" color="error-dark">
                      This promo code is in use by{' '}
                      <Link color="error-dark" href={existingSpecialLink}>
                        {existingSpecial.title || ''}
                      </Link>
                    </Typography>
                  </Flex>
                }
                icon={<AlertIcon />}
              />
            )}
            {isLoading ? (
              <Skeleton animate>
                <Skeleton.Bone width="30%" height={BODY_LINE_HEIGHT} />
              </Skeleton>
            ) : (
              <>
                <Form.SwitchField
                  label="Enable promo code"
                  name="display.promo_code_enabled"
                  mb={switchEnabled ? 40 : 0}
                  disabled={isDisabled}
                />
                {switchEnabled && (
                  <>
                    <Flex
                      alignItems="center"
                      gap={24}
                      mb={FORM_FIELD_DEFAULT_MARGIN}
                    >
                      <Form.TextField
                        name="promo_code"
                        label="Promo code"
                        width="100%"
                        maxLength={PROMO_CODE_MAX_LENGTH}
                        disabled={isDisabled}
                        onChange={validateUniquePromoCode}
                        validate={() => validationError || true}
                        // Don't remove field from formData when switch is disabled, otherwise it will lose any changes to these fields
                        shouldUnregister={false}
                      />
                      <Form.CheckboxField
                        mt={32}
                        width="100%"
                        name="multiple_use_promo_code"
                        label="Customers can use this promo code multiple times"
                        disabled={isDisabled}
                        // Don't remove field from formData when switch is disabled, otherwise it will lose any changes to these fields
                        shouldUnregister={false}
                      />
                    </Flex>
                    <Flex gap={24} mb={FORM_FIELD_DEFAULT_MARGIN}>
                      <Form.NumberField
                        width="100%"
                        name="promo_code_max_number_of_uses"
                        label="Maximum number of uses across all customers (optional)"
                        helperText="Leave blank to not restrict number of uses."
                        // TODO: This validation doesn't work for the value 0
                        min={0}
                        disabled={isDisabled}
                        // Don't remove field from formData when switch is disabled, otherwise it will lose any changes to these fields
                        shouldUnregister={false}
                        disableOnWheel
                      />
                      {/* Needed to get the spacing right */}
                      <Flex width="100%">&nbsp;</Flex>
                    </Flex>
                  </>
                )}
              </>
            )}
          </>
        </Flex>
      </Card.Content>
    </Card>
  );
};
